import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit
} from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { assign, each, intersection, isArray, isNil, map } from 'lodash';
import { concatMap, of } from 'rxjs';
import { CardColumnSelectionBase } from 'src/app/classes/base-classes/card-column/card-column-selection-base';
import { VendorForm } from 'src/app/classes/form-groups/vendor-form';
import { Company, Vendor } from 'src/app/classes/models';
import { SubNationalDivision } from 'src/app/classes/models/sub-national-division';
import { VendorTradeState } from 'src/app/classes/models/vendor-trade-state';
import { ArraySelectOption } from 'src/app/interfaces/array-select-option';
import { Card } from 'src/app/interfaces/card';
import { LocalizationInfo } from 'src/app/interfaces/localization-info';
import { State } from 'src/app/interfaces/state';
import { SharedDataService } from 'src/app/services/shared-data.service';
import { SubNationalDivisionService } from 'src/app/services/sub-national-division.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';
import { VendorService } from 'src/app/services/vendor.service';

import { CardColumnDisplayModalComponent } from '../card-column-modal/card-column-display-modal/card-column-display-modal.component';

@Component({
  selector: 'app-vendor-trade-states-modal',
  templateUrl:
    '../card-column-modal/card-column-display-modal/card-column-display-modal.component.html',
  styleUrls: [
    '../card-column-modal/card-column-display-modal/card-column-display-modal.component.less',
    './vendor-trade-states-modal.component.less'
  ],
  providers: [SharedDataService],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
// This should absolutely be modified to make selection optional.
export class VendorTradeStatesModalComponent
  extends CardColumnDisplayModalComponent
  implements OnInit
{
  constructor(
    protected override activeModal: NgbActiveModal,
    private VendorService: VendorService,
    private SubNationalDivisionService: SubNationalDivisionService,
    private UtilityService: UtilityService,
    protected override ToastService: ToastService,
    protected override ChangeDetectorRef: ChangeDetectorRef,
    protected override SharedDataService: SharedDataService
  ) {
    super(activeModal, ToastService, ChangeDetectorRef, SharedDataService);
  }

  @Input() VendorId!: number;
  @Input() CompanyIds: number[] = [];

  @Input() VendorTradeStates!: VendorTradeState[];
  @Input() Vendor?: Vendor;

  // Limit trade state selection based on the Vendor's selected service area.
  @Input() VendorFormGroup!: VendorForm;

  // Can optionally pass in a map of SubNationalDivisions (index is SubNationalDivision id) to prevent
  // selection/deselection of a SubNationalDivision
  @Input() selectionDisabledStates?: Set<State>;

  Companies: Company[] = [];
  SubNationalDivisions!: Required<SubNationalDivision>[];
  localizationInfo!: LocalizationInfo;
  country!: 'US' | 'CA';

  // Leave here for the time being. Client operationStates should be migrated to using
  // SubNationalDivisionId w/ lookup table.
  // @Input() selectionDisabledSubNationalDivisions?: { [index: number]: SubNationalDivision };

  // Determines if the Vendor's trade state selection is limited to their physical (street) address.
  isLimitedToStreetAddressStateProvinceTerritory() {
    const {
      value: { serviceArea }
    } = this.VendorFormGroup;

    return new Set([
      '0-25 Miles',
      '0-50 Miles',
      '0-100 Miles',
      '0-500 Miles',
      'County Wide',
      'State Wide',
      '0-40 Kilometers',
      '0-80 Kilometers',
      '0-160 Kilometers',
      '0-805 Kilometers',
      'Province/Territory-Wide'
    ]).has(serviceArea!);
  }

  // If the Vendor record has been passed to this component, use it (convert it into an observable
  // first). Otherwise, use the Vendor service to return an observable.
  returnVendorObservable() {
    if (this.Vendor) {
      return of(this.Vendor);
    }

    const {
      value: { serviceArea }
    } = this.VendorFormGroup;

    return this.VendorService.getVendorV2(this.VendorId, {
      attributes: JSON.stringify(['id', 'country']),
      parameterizedAttributes: JSON.stringify({
        vendorDistanceFromCities: {
          parameters: { VendorId: this.VendorId, serviceArea }
        }
      }),
      // aliasedAttributes: JSON.stringify(['vendorDistanceFromCities']),
      StreetAddress: JSON.stringify({
        attributes: ['id', 'address1', 'address2', 'city', 'state', 'zip']
      }),
      CompanyVendor: JSON.stringify({
        attributes: [
          'id',
          'CompanyId',
          'VendorId',
          'CompanyComplianceTypeId',
          'country'
        ],
        CompanyId: { $in: this.CompanyIds },
        Company: {
          options: { required: true },
          attributes: ['id', 'legalName'],
          CompanyOperationState: {
            SubNationalDivision: {}
          }
        }
      }),
      VendorTradeState: JSON.stringify({
        SubNationalDivision: {}
      })
    });
  }

  override ngOnInit(): void {
    this.enableFiltering = true;

    if (!this.VendorFormGroup) {
      throw this.ToastService.addErrorToast('Error', 'Missing Vendor form.', {
        returnError: true
      });
    }

    const {
      value: { serviceArea }
    } = this.VendorFormGroup;

    if (!serviceArea) {
      throw this.ToastService.addErrorToast('Error', 'Missing service area.', {
        returnError: true
      });
    }

    if (!isNil(this.Vendor)) {
      const { id } = this.Vendor;

      if (!id) {
        throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
          returnError: true
        });
      }

      this.VendorId = id;
    } else {
      if (!this.VendorId) {
        throw this.ToastService.addErrorToast('Error', 'Missing Vendor Id.', {
          returnError: true
        });
      }
      if (!this.CompanyIds) {
        throw this.ToastService.addErrorToast(
          'Error',
          'Missing Client Id(s).',
          { returnError: true }
        );
      }
    }

    this.returnVendorObservable().subscribe({
      next: (Vendor: Vendor) => {
        if (!Vendor) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor information.',
            { returnError: true }
          );
        }
        if (!this.Vendor) {
          this.Vendor = Vendor;
        }

        const {
          vendorDistanceFromCities,
          country,
          CompanyVendors,
          VendorTradeStates,
          StreetAddress
        } = this.Vendor;
        this.VendorTradeStates =
          this.VendorTradeStates || VendorTradeStates || [];

        // Extract all of the Sub National Divisions from the Vendor Trade States.
        // This will be used to process the pre-selected options in the modal.
        const VendorTradeStateSubNationalDivisions: SubNationalDivision[] = [];
        this.VendorTradeStates.forEach((vts) => {
          const { SubNationalDivision } = vts;
          if (!isNil(SubNationalDivision)) {
            VendorTradeStateSubNationalDivisions.push(SubNationalDivision);
          }
        });

        if (country) {
          this.localizationInfo =
            this.UtilityService.getLocalizedValuesMap(country);
        } else {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing country information',
            { returnError: true }
          );
        }

        if (!StreetAddress) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing street address.',
            { returnError: true }
          );
        }

        const { state } = StreetAddress;

        if (!state) {
          throw this.ToastService.addErrorToast(
            'Error',
            `Missing street address ${this.localizationInfo.territory.label}`,
            { returnError: true }
          );
        }

        Object.assign(this, { country });

        if (!Array.isArray(CompanyVendors) || CompanyVendors.length === 0) {
          throw this.ToastService.addErrorToast(
            'Error',
            'Missing Client/Vendor information.',
            { returnError: true }
          );
        }

        const clientLegalNames: string[] = [];
        const operationStatesSet: Set<State> = new Set<State>();
        CompanyVendors.forEach((CompanyVendor) => {
          const { Company } = CompanyVendor;

          if (isNil(Company)) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Missing Client information.',
              { returnError: true }
            );
          }

          this.Companies.push(Company);

          const { legalName, CompanyOperationStates } = Company;
          if (!legalName) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Client is missing legal name.',
              { returnError: true }
            );
          }
          clientLegalNames.push(legalName);

          if (!Array.isArray(CompanyOperationStates)) {
            throw this.ToastService.addErrorToast(
              'Error',
              'Client operation states have not been set.',
              { returnError: true }
            );
          }

          CompanyOperationStates.forEach((CompanyOperationState) => {
            const { SubNationalDivision } = CompanyOperationState;

            if (!SubNationalDivision) {
              throw this.ToastService.addErrorToast(
                'Error',
                'There was an error loading the Sub National Division',
                { returnError: true }
              );
            }

            const { abbreviation } = SubNationalDivision;

            if (!abbreviation) {
              throw this.ToastService.addErrorToast(
                'Error',
                'Missing state information.',
                { returnError: true }
              );
            }

            operationStatesSet.add(abbreviation);
          });
        });

        const ClientVendorStates: State[] = [];

        if (this.isLimitedToStreetAddressStateProvinceTerritory()) {
          // Rather than using the Vendor's street address, we will instead pass in a collection
          // of states that are within the Vendor's selected operation distance.
          ClientVendorStates.push(
            ...intersection(
              Array.from(operationStatesSet),
              (vendorDistanceFromCities || '').split(',') as State[]
            )
          );
        } else {
          ClientVendorStates.push(...Array.from(operationStatesSet));
        }

        // Update for Canada
        this.title = `Select the states you are willing to service for ${clientLegalNames.join(', ')}.`;
        this.message = `Only ${this.localizationInfo.stateProvincePlural.label} in which ${clientLegalNames.join(', ')} operates will appear below.`;

        this.SubNationalDivisionService.getSubNationalDivisions({
          country: country,
          abbreviation: JSON.stringify({ $in: ClientVendorStates })
        }).subscribe({
          next: (SubNationalDivisions: SubNationalDivision[]) => {
            if (!isArray(SubNationalDivisions)) {
              throw this.ToastService.addErrorToast(
                'Error',
                `${country === 'US' ? 'States' : 'Territories/Provinces'} information is not an array.`,
                { returnError: true }
              );
            }

            if (SubNationalDivisions.length === 0) {
              this.AdditionalMessages.push({
                classes: 'error-red',
                text: `No ${this.localizationInfo.stateProvincePlural.label} found within Service Area Distance. Please go back and try increasing Service Area Distance to Multi-state or Nationwide.`
              });
              // May need to modify this.
              this.VendorFormGroup.controls['serviceArea'].setErrors({
                increaseDistance: true
              });
              this.VendorFormGroup.updateValueAndValidity();
              this.VendorFormGroup.controls['serviceArea'].markAsDirty();
            }

            this.SubNationalDivisions =
              SubNationalDivisions as Required<SubNationalDivision>[];

            this.selectionModel = new CardColumnSelectionBase(
              'multiple',
              VendorTradeStateSubNationalDivisions
            );

            this.cardCollection = map(
              this.SubNationalDivisions,
              (SubNationalDivision) => {
                const card = {
                  bodyText: SubNationalDivision.name,
                  record: {
                    value: SubNationalDivision,
                    comparator: (compareValue, selectValue) =>
                      compareValue.id === selectValue.id
                  } as ArraySelectOption<SubNationalDivision>
                } as Card;

                // Bring in Client Operation States across all Clients paid for. Prevent deselection for
                // these Clients when the Vendor is registering for a new Client.
                if (this.selectionDisabledStates) {
                  assign(card, {
                    preventSelection: this.selectionDisabledStates.has(
                      SubNationalDivision.abbreviation
                    )
                  });
                }

                return card;
              }
            );

            super.ngOnInit();
            this.ChangeDetectorRef.markForCheck();
          }
        });
      }
    });
  }
}
